import React, { useState } from 'react';
import { PlusCircle, MinusCircle, Calculator, Check } from 'lucide-react';

// TypeScript interfaces
interface Integration {
  id: number;
  category: string;
  name: string;
  price: number;
}

interface IntegrationCategory {
  name: string;
  options: string[];
  pricePerItem: number;
  multiSelect: boolean;
}

interface SelectedSingleOptions {
  [key: string]: string | null;
}

const App = () => {
  // State pentru selecțiile utilizatorului
  const [orderVolume, setOrderVolume] = useState<number>(100);
  const [activeItems, setActiveItems] = useState<number>(500);
  const [selectedIntegrations, setSelectedIntegrations] = useState<Integration[]>([]);
  const [showSummary, setShowSummary] = useState<boolean>(false);
  const [allSaasOption, setAllSaasOption] = useState<boolean>(false);
  
  // State pentru selectarea unei singure opțiuni în categoriile specificate
  const [selectedSingleOptions, setSelectedSingleOptions] = useState<SelectedSingleOptions>({
    "Sisteme ERP": null,
    "Sisteme WMS": null,
    "Retail POS": null,
    "Aplicații facturare": null
  });

  // Calculează prețul abonamentului bazat pe comenzi și articole
  const calculateSubscriptionPrice = (): number => {
    // Prețul de bază: 38€ pentru 100 comenzi și 500 articole
    const basePrice = 38;
    
    // Calculează costul suplimentar pentru comenzi
    // 8€ pentru fiecare 100 comenzi peste limita de bază
    const extraOrderCost = Math.max(0, Math.ceil((orderVolume - 100) / 100)) * 8;
    
    // Calculează costul suplimentar pentru articole
    let extraItemCost = 0;
    
    if (activeItems > 500) {
      // Pentru primele 2000 articole (până la 2500 total): 4€ per 500 articole
      const firstTierItems = Math.min(activeItems, 2500) - 500;
      const firstTierCost = Math.ceil(firstTierItems / 500) * 4;
      
      // Pentru articolele peste 2500: 2€ per 1000 articole
      let secondTierItems = 0;
      if (activeItems > 2500) {
        secondTierItems = activeItems - 2500;
        const secondTierCost = Math.ceil(secondTierItems / 1000) * 2;
        extraItemCost = firstTierCost + secondTierCost;
      } else {
        extraItemCost = firstTierCost;
      }
    }
    
    // Prețul total al abonamentului
    return basePrice + extraOrderCost + extraItemCost;
  };

  // Determină numele planului bazat pe volumul de comenzi și articole
  const getPlanName = (): string => {
    if (orderVolume <= 200 && activeItems <= 1000) {
      return "Starter";
    } else if (orderVolume <= 1000 && activeItems <= 5000) {
      return "Business";
    } else {
      return "Enterprise";
    }
  };

  // Obține detaliile planului
  const getRecommendedPlan = () => {
    const price = calculateSubscriptionPrice();
    const name = getPlanName();
    
    return {
      name,
      price,
      orderLimit: Math.ceil(orderVolume / 100) * 100,
      itemLimit: Math.ceil(activeItems / 500) * 500
    };
  };

  // Categorii de integrări
  const integrationCategories: IntegrationCategory[] = [
    {
      name: "Platforme eCommerce",
      options: ["PrestaShop", "Shopify", "OpenCart", "csCart", "MerchantPro", "Magento", "Magento 2", "WooCommerce", "BigCommerce"],
      pricePerItem: 690,
      multiSelect: true
    },
    {
      name: "Marketplace-uri",
      options: ["eMAG Marketplace", "Trendyol", "Temu", "Amazon", "Allegro", "Altex", "CEL Marketplace"],
      pricePerItem: 690,
      multiSelect: true
    },
    {
      name: "Sisteme ERP",
      options: ["Wizrom", "Charisma ERP", "SeniorERP", "Dynamics NAV", "SAP Business One", "Navision", "Axapta", "B-ORG ERP", "WinMentor Enterprise", "SoftOne ERP", "Entersoft ERP", "Pluriva ERP", "Asis ERP", "Odoo ERP", "Nexus ERP"],
      pricePerItem: 1780,
      multiSelect: false
    },
    {
      name: "Sisteme WMS",
      options: ["Mantis LVS", "Axea xTrack WMS", "Cross Point WMS"],
      pricePerItem: 1780,
      multiSelect: false
    },
    {
      name: "Sisteme CRM",
      options: ["Salesforce", "Microsoft Dynamics", "Zoho CRM", "Hubspot", "AMIRO CRM", "SugarCRM"],
      pricePerItem: 1780,
      multiSelect: true
    },
    {
      name: "Retail POS",
      options: ["Magister SmartCash", "Sedona Retail", "Spectrum", "RBC Retail"],
      pricePerItem: 100,
      multiSelect: false
    },
    {
      name: "Aplicații facturare",
      options: ["SmartBill", "FGO", "Oblio", "Facturis", "EasyBill"],
      pricePerItem: 120,
      multiSelect: false
    },
    {
      name: "Curieri",
      options: ["Fan Courier", "Cargus", "DPD Romania", "Sameday", "Urgent Cargus", "GLS Romania", "Innoship", "Postis"],
      pricePerItem: 100,
      multiSelect: true
    }
  ];

  // Adăugarea unei integrări
  const addIntegration = (category: IntegrationCategory, option: string): void => {
    // Pentru categoriile cu selecție unică
    if (!category.multiSelect) {
      // Verifică dacă există deja o integrare pentru această categorie
      const existingIntegration = selectedIntegrations.find(
        integration => integration.category === category.name
      );
      
      if (existingIntegration) {
        // Înlocuiește integrarea existentă
        setSelectedIntegrations(
          selectedIntegrations.map(integration => 
            integration.category === category.name
              ? { ...integration, name: option, id: Date.now() }
              : integration
          )
        );
      } else {
        // Adaugă o nouă integrare
        const integration: Integration = {
          id: Date.now(),
          category: category.name,
          name: option,
          price: category.pricePerItem
        };
        setSelectedIntegrations([...selectedIntegrations, integration]);
      }
      
      // Actualizează starea pentru radio buttons
      setSelectedSingleOptions({
        ...selectedSingleOptions,
        [category.name]: option
      });
    } else {
      // Pentru categoriile multi-select, comportamentul rămâne neschimbat
      const integration: Integration = {
        id: Date.now(),
        category: category.name,
        name: option,
        price: category.pricePerItem
      };
      setSelectedIntegrations([...selectedIntegrations, integration]);
    }
  };

  // Eliminarea unei integrări
  const removeIntegration = (id: number): void => {
    const integrationToRemove = selectedIntegrations.find(item => item.id === id);
    
    // Dacă este o integrare din categorie cu selecție unică, actualizează și starea radio button-urilor
    if (integrationToRemove) {
      const category = integrationCategories.find(cat => cat.name === integrationToRemove.category);
      if (category && !category.multiSelect) {
        setSelectedSingleOptions({
          ...selectedSingleOptions,
          [integrationToRemove.category]: null
        });
      }
    }
    
    setSelectedIntegrations(selectedIntegrations.filter(item => item.id !== id));
  };

  // Calcularea costului total
  const calculateTotal = () => {
    const plan = getRecommendedPlan();
    const subscriptionCost = plan.price;
    const integrationCost = selectedIntegrations.reduce((sum, item) => sum + item.price, 0);
    
    // Calculează costul lunar SaaS pentru integrări (împărțind la 36 de luni)
    const monthlySaasCost = allSaasOption ? Math.ceil(integrationCost / 36) : 0;
    
    return {
      subscriptionCost,
      integrationCost,
      monthlySaasCost,
      total: subscriptionCost + (allSaasOption ? monthlySaasCost : 0)
    };
  };

  const totalCost = calculateTotal();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-700 to-indigo-800 py-8">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white">One Platform</h1>
              <p className="text-blue-100 mt-2">Soluția completă de integrare iPaaS pentru afacerea ta</p>
            </div>
            <div className="hidden md:block">
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white border-opacity-20">
                <span className="text-white font-semibold">Calculator de ofertă</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="p-6 pb-3">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Configurează abonamentul tău</h2>
            
            {/* Subscription Calculator */}
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-lg font-medium text-gray-700 mb-4">1. Selectează volumul de comenzi și articole</h3>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-600 mb-2">
                    Număr comenzi zilnice: <span className="text-blue-700 font-semibold">{orderVolume}</span>
                  </label>
                  <input 
                    type="range" 
                    min="10" 
                    max="5000" 
                    step="10"
                    value={orderVolume} 
                    onChange={e => setOrderVolume(parseInt(e.target.value))}
                    className="w-full h-2 bg-blue-100 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>10</span>
                    <span>5000</span>
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-600 mb-2">
                    Număr articole active: <span className="text-blue-700 font-semibold">{activeItems}</span>
                  </label>
                  <input 
                    type="range" 
                    min="100" 
                    max="50000" 
                    step="100"
                    value={activeItems} 
                    onChange={e => setActiveItems(parseInt(e.target.value))}
                    className="w-full h-2 bg-blue-100 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>100</span>
                    <span>50000</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-700 mb-4">Planul recomandat</h3>
                
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
                  <h4 className="text-xl font-semibold text-blue-700">{getRecommendedPlan().name}</h4>
                  <ul className="mt-3 space-y-2">
                    <li className="flex items-center">
                      <Check size={16} className="text-green-500 mr-2" />
                      <span>Până la {getRecommendedPlan().orderLimit} comenzi zilnice</span>
                    </li>
                    <li className="flex items-center">
                      <Check size={16} className="text-green-500 mr-2" />
                      <span>Până la {getRecommendedPlan().itemLimit} articole active</span>
                    </li>
                    <li className="flex items-center">
                      <Check size={16} className="text-green-500 mr-2" />
                      <span>Suport tehnic prioritar</span>
                    </li>
                    <li className="flex items-center">
                      <Check size={16} className="text-green-500 mr-2" />
                      <span>Actualizări automate</span>
                    </li>
                  </ul>
                  
                  <div className="mt-4 text-center">
                    <span className="text-3xl font-bold text-blue-700">{getRecommendedPlan().price}€</span>
                    <span className="text-gray-500 ml-1">/lună</span>
                  </div>
                  
                  <div className="mt-2 text-center text-xs text-gray-500">
                    Preț personalizat în funcție de volumul tău
                  </div>
                </div>
                
                {/* Selected Integrations - Moved here from below */}
                {selectedIntegrations.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-lg font-medium text-gray-700 mb-4">Integrări selectate</h3>
                    
                    <div className="bg-white border border-gray-200 rounded-lg">
                      <div className="p-3 bg-gray-50 border-b border-gray-200 flex justify-between">
                        <span className="font-medium text-gray-700">Integrare</span>
                        <span className="font-medium text-gray-700">Preț</span>
                      </div>
                      
                      <div className="max-h-60 overflow-y-auto">
                        {selectedIntegrations.length === 0 ? (
                          <div className="p-4 text-center text-gray-500">Nu ați selectat nicio integrare</div>
                        ) : (
                          <div>
                            {selectedIntegrations.map(integration => (
                              <div key={integration.id} className="flex justify-between items-center p-3 border-b border-gray-100 hover:bg-gray-50">
                                <div className="flex items-center">
                                  <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                                  <span className="text-sm font-medium">{integration.name}</span>
                                  <span className="text-xs text-gray-500 ml-2">({integration.category})</span>
                                </div>
                                <div className="flex items-center">
                                  <span className="text-sm font-semibold mr-3">{integration.price}€</span>
                                  <button 
                                    onClick={() => removeIntegration(integration.id)}
                                    className="text-gray-400 hover:text-red-500 transition-colors"
                                  >
                                    <MinusCircle size={16} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <div className="p-3 bg-gray-50 border-t border-gray-200 flex justify-between">
                        <span className="font-medium text-gray-700">Total integrări</span>
                        <span className="font-bold text-blue-700">{totalCost.integrationCost}€</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          
            {/* Integration Selection */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-700">2. Adaugă integrările dorite</h3>
                <div className="flex items-center">
                  <input 
                    type="checkbox" 
                    id="allSaasOption" 
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    onChange={(e) => setAllSaasOption(e.target.checked)}
                    checked={allSaasOption}
                  />
                  <label htmlFor="allSaasOption" className="ml-2 text-sm text-gray-700">
                    All SaaS (plată lunară)
                  </label>
                  <div className="ml-2 group relative">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 cursor-help" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                    <div className="absolute z-10 w-64 p-2 bg-white rounded-lg shadow-lg border border-gray-200 text-xs text-gray-600 invisible group-hover:visible right-0 mt-1">
                      Opțiunea All SaaS transformă costul one-time al integrărilor într-un abonament lunar, împărțind suma la 36 luni.
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mb-6">
                {integrationCategories.map((category, categoryIndex) => (
                  <div key={categoryIndex} className="mb-4">
                    <div className="flex items-center justify-between bg-blue-50 px-4 py-2 rounded-t-lg border border-blue-100">
                      <h4 className="font-medium text-blue-800">{category.name}</h4>
                      <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                        {category.pricePerItem}€ per integrare
                      </span>
                    </div>
                    
                    <div className="border border-t-0 border-gray-200 rounded-b-lg p-2 bg-white">
                      {category.multiSelect ? (
                        // Pentru categorii cu selecție multiplă - butoane cu plus
                        <div className="flex flex-wrap gap-2">
                          {category.options.map((option, optionIndex) => {
                            // Verificăm dacă această opțiune este deja selectată
                            const isSelected = selectedIntegrations.some(
                              integration => integration.category === category.name && integration.name === option
                            );
                            
                            return (
                              <button 
                                key={optionIndex}
                                onClick={() => {
                                  // Dacă este deja selectată, o eliminăm
                                  if (isSelected) {
                                    const integrationToRemove = selectedIntegrations.find(
                                      integration => integration.category === category.name && integration.name === option
                                    );
                                    if (integrationToRemove) {
                                      removeIntegration(integrationToRemove.id);
                                    }
                                  } else {
                                    // Dacă nu este selectată, o adăugăm
                                    addIntegration(category, option);
                                  }
                                }}
                                className={`flex items-center text-xs ${
                                  isSelected 
                                    ? 'bg-blue-600 hover:bg-blue-700 text-white border border-blue-700' 
                                    : 'bg-gray-50 hover:bg-blue-50 text-gray-700 border border-gray-200 hover:border-blue-200'
                                } rounded-full px-3 py-1 transition-colors`}
                              >
                                <span>{option}</span>
                                {isSelected ? (
                                  <MinusCircle size={14} className="ml-1 text-white" />
                                ) : (
                                  <PlusCircle size={14} className="ml-1 text-blue-600" />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        // Pentru categorii cu selecție unică - radio buttons
                        <div className="grid grid-cols-2 gap-2">
                          {category.options.map((option, optionIndex) => (
                            <div 
                              key={optionIndex}
                              className={`flex items-center border rounded p-2 cursor-pointer ${
                                selectedSingleOptions[category.name] === option 
                                  ? 'bg-blue-50 border-blue-300' 
                                  : 'bg-white border-gray-200 hover:bg-gray-50'
                              }`}
                              onClick={() => addIntegration(category, option)}
                            >
                              <div className={`w-4 h-4 rounded-full border mr-2 flex items-center justify-center ${
                                selectedSingleOptions[category.name] === option 
                                  ? 'border-blue-500' 
                                  : 'border-gray-400'
                              }`}>
                                {selectedSingleOptions[category.name] === option && (
                                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                                )}
                              </div>
                              <span className="text-sm">{option}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Cost Summary Button */}
          <div className="mt-4 py-4 bg-gray-50 border-t border-gray-200 px-6">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-sm text-gray-500">Total estimat:</div>
                <div className="text-lg font-bold text-blue-700">
                  {allSaasOption ? (
                    `${totalCost.subscriptionCost + totalCost.monthlySaasCost}€/lună`
                  ) : (
                    <>
                      {totalCost.integrationCost > 0 ? `${totalCost.integrationCost}€ one-time + ` : ''}
                      {totalCost.subscriptionCost}€<span className="text-sm font-normal text-gray-600">/lună</span>
                    </>
                  )}
                </div>
                {allSaasOption && totalCost.integrationCost > 0 && (
                  <div className="text-xs text-gray-600">
                    *Include {totalCost.monthlySaasCost}€/lună pentru integrări (SaaS)
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowSummary(true)}
                className="flex items-center bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                <Calculator className="mr-2" size={20} />
                Calculează oferta
              </button>
            </div>
          </div>
        </div>
        
        {/* Summary Modal */}
        {showSummary && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-auto">
            <div className="bg-white rounded-lg shadow-xl p-0 max-w-4xl w-full mx-4" id="printSection">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-1">Oferta ta personalizată</h3>
                    <p className="text-blue-100 text-sm">One Platform iPaaS</p>
                  </div>
                  <div className="text-white text-sm">
                    Data: {new Date().toLocaleDateString('ro-RO')}
                  </div>
                </div>
              </div>
              
              <div className="p-8">
                <div className="space-y-6">
                  <div className="flex flex-col bg-blue-50 rounded-lg p-6">
                    <div className="flex justify-between mb-2">
                      <span className="text-gray-700 font-medium text-lg">Plan de abonament</span>
                      <span className="font-bold text-blue-700 text-lg">{totalCost.subscriptionCost}€/lună</span>
                    </div>
                    <span className="text-xl font-semibold text-blue-800 mb-3">{getRecommendedPlan().name}</span>
                    <div className="text-base text-gray-600 grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="flex items-center">
                        <Check size={18} className="text-green-500 min-w-[1.5rem] mr-2" />
                        <span>Până la {getRecommendedPlan().orderLimit} comenzi zilnice</span>
                      </div>
                      <div className="flex items-center">
                        <Check size={18} className="text-green-500 min-w-[1.5rem] mr-2" />
                        <span>Până la {getRecommendedPlan().itemLimit} articole active</span>
                      </div>
                      <div className="flex items-center">
                        <Check size={18} className="text-green-500 min-w-[1.5rem] mr-2" />
                        <span>Suport tehnic prioritar</span>
                      </div>
                      <div className="flex items-center">
                        <Check size={18} className="text-green-500 min-w-[1.5rem] mr-2" />
                        <span>Actualizări automate</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t border-b border-gray-200 py-6">
                    <div className="flex justify-between mb-4">
                      <span className="text-gray-700 font-medium text-lg">Integrări</span>
                      {allSaasOption ? (
                        <span className="font-bold text-blue-700 text-lg">{totalCost.monthlySaasCost}€/lună</span>
                      ) : (
                        <span className="font-bold text-blue-700 text-lg">{totalCost.integrationCost}€ one-time</span>
                      )}
                    </div>
                    
                    {selectedIntegrations.length > 0 ? (
                      <div className="mt-3 max-h-60 overflow-y-auto">
                        <table className="w-full border-collapse">
                          <thead className="bg-gray-50">
                            <tr className="border-b">
                              <th className="text-left py-2 px-3 text-gray-700">Integrare</th>
                              <th className="text-left py-2 px-3 text-gray-700">Categorie</th>
                              <th className="text-right py-2 px-3 text-gray-700">
                                {allSaasOption ? 'Preț lunar' : 'Preț'}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedIntegrations.map((integration, index) => (
                              <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="py-3 px-3 text-gray-700">{integration.name}</td>
                                <td className="py-3 px-3 text-gray-600">{integration.category}</td>
                                <td className="py-3 px-3 text-gray-800 text-right">
                                  {allSaasOption 
                                    ? `${Math.ceil(integration.price / 36)}€/lună` 
                                    : `${integration.price}€`
                                  }
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-base text-gray-500 italic p-4 text-center">Nu ați selectat nicio integrare</div>
                    )}
                  </div>
                  
                  <div className="pt-4">
                    <div className="flex justify-between text-xl font-bold">
                      <span>Total</span>
                      <div className="text-right">
                        {allSaasOption ? (
                          <div className="text-blue-700">
                            {totalCost.subscriptionCost + totalCost.monthlySaasCost}€ <span className="text-base font-normal text-gray-600">/lună</span>
                            {totalCost.monthlySaasCost > 0 && (
                              <div className="text-sm font-normal text-gray-600 mt-1">
                                *Include {totalCost.monthlySaasCost}€/lună pentru integrări (SaaS)
                              </div>
                            )}
                          </div>
                        ) : (
                          <>
                            <div className="text-blue-700">{totalCost.integrationCost}€ <span className="text-base font-normal text-gray-600">one-time</span></div>
                            <div className="text-blue-700">+ {totalCost.subscriptionCost}€ <span className="text-base font-normal text-gray-600">/lună</span></div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200 pt-6 mt-6">
                    <div className="text-sm text-gray-600">
                      <p><strong>Notă:</strong> Această ofertă este valabilă 30 de zile de la data emiterii.</p>
                      <p>Pentru mai multe detalii, vă rugăm să ne contactați la <span className="text-blue-600">contact@oneplatform.ro</span></p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="p-6 bg-gray-50 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row justify-between space-y-4 sm:space-y-0">
                  <div className="space-x-3">
                    <button
                      onClick={() => setShowSummary(false)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 font-medium"
                    >
                      Înapoi la configurare
                    </button>
                    
                    <button
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium"
                    >
                      Salvează oferta
                    </button>
                  </div>
                  
                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        // Includem script-ul html2canvas
                        const script = document.createElement('script');
                        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
                        script.onload = function() {
                          // Obținem elementul de capturat
                          const element = document.getElementById('printSection');
                          
                          if (element) {
                            // Adăugăm o clasă temporară pentru a elimina scrollbar-ul și îmbunătăți captura
                            element.classList.add('overflow-visible');
                            
                            // @ts-ignore - html2canvas is loaded dynamically
                            (window as any).html2canvas(element, {
                              scale: 2, // Calitate mai bună
                              useCORS: true,
                              backgroundColor: '#ffffff',
                              logging: false,
                              allowTaint: true
                            }).then((canvas: HTMLCanvasElement) => {
                              // Restaurăm clasa originală
                              element.classList.remove('overflow-visible');
                              
                              // Convertim canvas în URL de imagine
                              const imageURL = canvas.toDataURL('image/jpeg', 1.0);
                              
                              // Creăm un element de link pentru descărcare
                              const downloadLink = document.createElement('a');
                              downloadLink.href = imageURL;
                              downloadLink.download = `Oferta_One_Platform_${new Date().toLocaleDateString('ro-RO').replace(/\//g, '-')}.jpg`;
                              
                              // Declanșăm descărcarea
                              document.body.appendChild(downloadLink);
                              downloadLink.click();
                              document.body.removeChild(downloadLink);
                            }).catch((err: Error) => {
                              console.error('Eroare la generarea imaginii:', err);
                              alert('A apărut o eroare la generarea imaginii. Vă rugăm să încercați din nou.');
                            });
                          }
                        };
                        
                        script.onerror = function() {
                          alert('Nu s-a putut încărca biblioteca necesară. Vă rugăm să verificați conexiunea la internet și să încercați din nou.');
                        };
                        
                        document.body.appendChild(script);
                      }}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium flex items-center"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Descarcă ca imagine
                    </button>
                    
                    <button
                      onClick={() => {
                        // Deschidem o fereastră nouă cu conținutul pentru printare
                        const offerHTML = `
                          <!DOCTYPE html>
                          <html>
                          <head>
                            <title>Ofertă One Platform</title>
                            <meta charset="utf-8">
                            <style>
                              body {
                                font-family: Arial, sans-serif;
                                padding: 40px;
                                color: #333;
                                max-width: 800px;
                                margin: 0 auto;
                              }
                              .header {
                                background: linear-gradient(to right, #2563eb, #4f46e5);
                                color: white;
                                padding: 20px;
                                border-radius: 8px 8px 0 0;
                              }
                              .content {
                                padding: 30px;
                                border: 1px solid #e5e7eb;
                                border-top: none;
                                border-radius: 0 0 8px 8px;
                              }
                              .plan {
                                background-color: #eff6ff;
                                padding: 20px;
                                border-radius: 8px;
                                margin-bottom: 24px;
                              }
                              .check-item {
                                display: flex;
                                align-items: center;
                                margin-bottom: 8px;
                              }
                              .check-mark {
                                color: #22c55e;
                                margin-right: 10px;
                              }
                              table {
                                width: 100%;
                                border-collapse: collapse;
                                margin: 20px 0;
                              }
                              th, td {
                                padding: 12px;
                                text-align: left;
                                border-bottom: 1px solid #e5e7eb;
                              }
                              th {
                                background-color: #f9fafb;
                              }
                              tr:nth-child(even) {
                                background-color: #f9fafb;
                              }
                              .footer {
                                border-top: 1px solid #e5e7eb;
                                padding-top: 20px;
                                margin-top: 30px;
                                font-size: 14px;
                                color: #6b7280;
                              }
                            </style>
                          </head>
                          <body>
                            <div class="header">
                              <h2 style="margin: 0 0 4px 0; font-size: 24px;">Oferta ta personalizată</h2>
                              <p style="margin: 0; font-size: 14px;">One Platform iPaaS</p>
                              <div style="display: flex; justify-content: space-between; margin-top: 10px;">
                                <div></div>
                                <div style="font-size: 14px;">Data: ${new Date().toLocaleDateString('ro-RO')}</div>
                              </div>
                            </div>
                            
                            <div class="content">
                              <div class="plan">
                                <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                                  <span style="font-weight: 500; font-size: 18px;">Plan de abonament</span>
                                  <span style="font-weight: 700; color: #2563eb; font-size: 18px;">${totalCost.subscriptionCost}€/lună</span>
                                </div>
                                <h3 style="margin: 0 0 16px 0; font-size: 22px; color: #1e40af;">${getRecommendedPlan().name}</h3>
                                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
                                  <div class="check-item">
                                    <span class="check-mark">✓</span>
                                    <span>Până la ${getRecommendedPlan().orderLimit} comenzi zilnice</span>
                                  </div>
                                  <div class="check-item">
                                    <span class="check-mark">✓</span>
                                    <span>Până la ${getRecommendedPlan().itemLimit} articole active</span>
                                  </div>
                                  <div class="check-item">
                                    <span class="check-mark">✓</span>
                                    <span>Suport tehnic prioritar</span>
                                  </div>
                                  <div class="check-item">
                                    <span class="check-mark">✓</span>
                                    <span>Actualizări automate</span>
                                  </div>
                                </div>
                              </div>
                              
                              <div style="border-top: 1px solid #e5e7eb; border-bottom: 1px solid #e5e7eb; padding: 24px 0;">
                                <div style="display: flex; justify-content: space-between; margin-bottom: 16px;">
                                  <span style="font-weight: 500; font-size: 18px;">Integrări</span>
                                  <span style="font-weight: 700; color: #2563eb; font-size: 18px;">
                                    ${allSaasOption 
                                      ? `${totalCost.monthlySaasCost}€/lună` 
                                      : `${totalCost.integrationCost}€ one-time`
                                    }
                                  </span>
                                </div>
                                
                                ${selectedIntegrations.length > 0 ? `
                                  <table>
                                    <thead>
                                      <tr>
                                        <th>Integrare</th>
                                        <th>Categorie</th>
                                        <th style="text-align: right;">${allSaasOption ? 'Preț lunar' : 'Preț'}</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      ${selectedIntegrations.map((integration, index) => `
                                        <tr>
                                          <td>${integration.name}</td>
                                          <td>${integration.category}</td>
                                          <td style="text-align: right;">
                                            ${allSaasOption 
                                              ? `${Math.ceil(integration.price / 36)}€/lună` 
                                              : `${integration.price}€`
                                            }
                                          </td>
                                        </tr>
                                      `).join('')}
                                    </tbody>
                                  </table>
                                ` : `
                                  <div style="font-style: italic; text-align: center; color: #6b7280; padding: 16px;">
                                    Nu ați selectat nicio integrare
                                  </div>
                                `}
                              </div>
                              
                              <div style="padding-top: 16px;">
                                <div style="display: flex; justify-content: space-between; font-size: 20px; font-weight: 700;">
                                  <span>Total</span>
                                  <div style="text-align: right;">
                                    ${allSaasOption ? `
                                      <div style="color: #2563eb;">
                                        ${totalCost.subscriptionCost + totalCost.monthlySaasCost}€ <span style="font-weight: 400; font-size: 16px; color: #6b7280;">/lună</span>
                                        ${totalCost.monthlySaasCost > 0 ? `
                                          <div style="font-size: 14px; font-weight: 400; color: #6b7280; margin-top: 4px;">
                                            *Include ${totalCost.monthlySaasCost}€/lună pentru integrări (SaaS)
                                          </div>
                                        ` : ''}
                                      </div>
                                    ` : `
                                      <div style="color: #2563eb;">${totalCost.integrationCost}€ <span style="font-weight: 400; font-size: 16px; color: #6b7280;">one-time</span></div>
                                      <div style="color: #2563eb;">+ ${totalCost.subscriptionCost}€ <span style="font-weight: 400; font-size: 16px; color: #6b7280;">/lună</span></div>
                                    `}
                                  </div>
                                </div>
                              </div>
                              
                              <div class="footer">
                                <p><strong>Notă:</strong> Această ofertă este valabilă 30 de zile de la data emiterii.</p>
                                <p>Pentru mai multe detalii, vă rugăm să ne contactați la <span style="color: #2563eb;">contact@oneplatform.ro</span></p>
                              </div>
                            </div>
                            
                            <script>
                              // Auto-print when the page loads
                              window.onload = function() {
                                window.print();
                              };
                            </script>
                          </body>
                          </html>
                        `;
                        
                        // Deschidem o fereastră nouă cu conținutul
                        const printWindow = window.open('', '_blank');
                        if (printWindow) {
                          printWindow.document.write(offerHTML);
                          printWindow.document.close();
                        } else {
                          alert('Vă rugăm să permiteți ferestrele pop-up pentru a putea printa oferta.');
                        }
                      }}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      Printează oferta
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
      
      {/* Footer */}
      <footer className="bg-gray-800 text-white py-6">
        <div className="container mx-auto px-4 text-center">
          <p>&copy; 2025 One Platform. Toate drepturile rezervate.</p>
        </div>
      </footer>
    </div>
  );
};

export default App;
